import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import FacebookIcon from '@material-ui/icons/Facebook';
import InstagramIcon from '@material-ui/icons/Instagram';
import LanguageIcon from '@material-ui/icons/Language';
import TwitterIcon from '@material-ui/icons/Twitter';
import YouTubeIcon from '@material-ui/icons/YouTube';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import {
  fetchYowanPublicClassDetail,
  fetchYowanPublicSeminarDetail,
  fetchYowanCurrentMemberProfile,
  fetchYowanAdminMemberAccessList,
  submitYowanPublicClassScheduleApplication,
  submitYowanPublicSeminarSeriesApplication,
} from '../app/yowanContentClient';
import {
  executeYowanRecaptcha,
  getYowanRecaptchaDiagnosticMessage,
  getYowanRecaptchaErrorMessage,
  getYowanRecaptchaResponseErrorMessage,
  YOWAN_RECAPTCHA_ACTIONS,
} from '../app/yowanRecaptchaClient';
import { fetchYowanAccessMe } from '../app/yowanAuthClient';
const { getYowanRouteMeta } = require('../routes/manifest');

const formatSlotLabel = (slot) => {
  return slot?.title || slot?.fallbackLabel || slot?.datetimeLabel || slot?.room_name || '講座枠';
};

const formatSeminarApplyFeeSuffix = (feeLabel) => (
  feeLabel && feeLabel !== '未定' ? `（${feeLabel}）` : ''
);

const APPLICATION_MESSAGE_MAX_LENGTH = 500;

const instructorWebLinkLabels = {
  website: '公式サイト',
  facebook: 'Facebook',
  x: 'X',
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  other: 'リンク',
};

const normalizeInstructorWebLinkLabel = (label = '') => {
  if (label === 'Website') {
    return '公式サイト';
  }

  if (label === 'Link') {
    return 'リンク';
  }

  return label;
};

const InstructorWebLinkIcon = ({ platformKey }) => {
  if (platformKey === 'facebook') {
    return <FacebookIcon aria-hidden="true" />;
  }

  if (platformKey === 'instagram') {
    return <InstagramIcon aria-hidden="true" />;
  }

  if (platformKey === 'youtube') {
    return <YouTubeIcon aria-hidden="true" />;
  }

  if (platformKey === 'x') {
    return <TwitterIcon aria-hidden="true" />;
  }

  if (platformKey === 'tiktok') {
    return <span aria-hidden="true" className="yowan-event-detail-instructors__link-text-icon">TT</span>;
  }

  return <LanguageIcon aria-hidden="true" />;
};

const defaultDetailFormContent = {
  buttonLabel: '申し込む',
  labels: {
    name: 'お名前',
    nameKana: 'ふりがな',
    email: 'メールアドレス',
    phone: '電話番号',
    gender: '性別',
    ageRange: '年代',
    preferredSlot: '希望日時',
    preferredDate: '希望日',
    message: 'メッセージ',
  },
  options: {
    gender: ['女性', '男性', '回答しない'],
    ageRange: ['10代', '20代', '30代', '40代', '50代', '60代以上'],
  },
  errors: {
    required: 'この項目は必須です。',
  },
  submitLabel: '送信',
  pendingMessage: '送信処理を受け付けました。',
  submittingMessage: '申込を送信しています。画面を閉じずにお待ちください。',
  messageMaxLength: APPLICATION_MESSAGE_MAX_LENGTH,
};

const pickFirstActiveItem = (items = []) => items.find((item) => item.isActive !== false) || items[0] || null;

const formatClassScheduleFee = (feeLabel) => {
  if (!feeLabel) {
    return '未定';
  }

  return feeLabel === '未定' ? feeLabel : `1回 ${feeLabel}`;
};

const getClassScheduleLocationText = (schedule) => {
  if (!schedule) {
    return '会場は調整中です。';
  }

  if (schedule.locationIsPublic === false) {
    return schedule.locationLabel || schedule.publicLocationLabel || '会場は調整中です。';
  }

  return schedule.locationLabel || schedule.roomName || '会場は調整中です。';
};

const formatClassScheduleItem = (schedule, index) => ({
  id: schedule.id || `schedule-${index}`,
  title: schedule.datetimeLabel || schedule.scheduleLabel || `開催情報 ${index + 1}`,
  location: getClassScheduleLocationText(schedule),
  locationNote: schedule.publicLocationNote || '',
  capacity: schedule.capacity ? `${schedule.capacity}名` : '未定',
  fee: formatClassScheduleFee(schedule.feeLabel),
  instructor: schedule.instructorName || '-',
  isActive: schedule.isActive !== false,
  acceptsApplications: schedule.acceptsApplications !== false,
});

const buildFallbackClassScheduleItem = (content) => ({
  id: 'class-schedule-default',
  title: content?.body?.datetime || '開催情報',
  location: content?.body?.location || '会場は調整中です。',
  locationNote: '',
  capacity: content?.body?.capacity || '未定',
  fee: content?.body?.fee || '未定',
  instructor: content?.body?.instructor || '-',
});

const formatClassApplicationScheduleLabel = (schedule = {}) => (
  [schedule.title, schedule.location, schedule.instructor]
    .filter(Boolean)
    .filter((value) => value !== '-')
    .join(' / ')
);

const YOWAN_CANONICAL_ORIGIN = 'https://yowan.jp';
const YOWAN_DEFAULT_OG_IMAGE = `${YOWAN_CANONICAL_ORIGIN}/yowan/ogp.png`;

const normalizeMetaText = (value = '') => (
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
);

const truncateMetaText = (value = '', maxLength = 160) => {
  const normalized = normalizeMetaText(value);

  return normalized.length > maxLength ? normalized.slice(0, maxLength).trimEnd() : normalized;
};

const getAbsoluteYowanUrl = (value = '') => {
  const rawValue = String(value || '').trim();

  if (!rawValue) {
    return '';
  }

  if (/^https?:\/\//i.test(rawValue)) {
    return rawValue;
  }

  return `${YOWAN_CANONICAL_ORIGIN}${rawValue.startsWith('/') ? rawValue : `/${rawValue}`}`;
};

const getOrCreateMetaTag = (selector, createAttributes = {}) => {
  if (typeof document === 'undefined') {
    return null;
  }

  let tag = document.querySelector(selector);

  if (!tag) {
    tag = document.createElement('meta');
    Object.entries(createAttributes).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }

  return tag;
};

const setMetaContent = (selector, createAttributes, content) => {
  const tag = getOrCreateMetaTag(selector, createAttributes);

  if (tag) {
    tag.setAttribute('content', content || '');
  }
};

const setCanonicalHref = (href) => {
  if (typeof document === 'undefined') {
    return;
  }

  let tag = document.querySelector('link[rel="canonical"]');

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
};

const applyYowanDetailMetadata = ({
  title,
  description,
  canonicalPath,
  image,
  type = 'website',
}) => {
  if (typeof document === 'undefined') {
    return;
  }

  const safeTitle = normalizeMetaText(title) || '揺腕法';
  const safeDescription = truncateMetaText(description || safeTitle);
  const canonicalUrl = getAbsoluteYowanUrl(canonicalPath || '/');
  const imageUrl = getAbsoluteYowanUrl(image || YOWAN_DEFAULT_OG_IMAGE);

  document.title = `${safeTitle} | 揺腕法`;
  setCanonicalHref(canonicalUrl);
  setMetaContent('meta[name="description"]', { name: 'description' }, safeDescription);
  setMetaContent('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
  setMetaContent('meta[property="og:type"]', { property: 'og:type' }, type);
  setMetaContent('meta[property="og:title"]', { property: 'og:title' }, safeTitle);
  setMetaContent('meta[property="og:description"]', { property: 'og:description' }, safeDescription);
  setMetaContent('meta[property="og:image"]', { property: 'og:image' }, imageUrl);
  setMetaContent('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
  setMetaContent('meta[name="twitter:url"]', { name: 'twitter:url' }, canonicalUrl);
  setMetaContent('meta[name="twitter:title"]', { name: 'twitter:title' }, safeTitle);
  setMetaContent('meta[name="twitter:description"]', { name: 'twitter:description' }, safeDescription);
  setMetaContent('meta[name="twitter:image"]', { name: 'twitter:image' }, imageUrl);
};

const splitTextParagraphs = (value = '') => (
  String(value || '')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
);

const seminarPartNumberLabels = [
  '第一部',
  '第二部',
  '第三部',
  '第四部',
  '第五部',
  '第六部',
  '第七部',
  '第八部',
  '第九部',
  '第十部',
];

const getSeminarPartLabel = (index) => seminarPartNumberLabels[index] || `第${index + 1}部`;

const getSeminarSlotDateLabel = (slot = {}) => {
  const datetimeLabel = slot.datetimeLabel || '';
  const dateMatch = datetimeLabel.match(/^(.+?日（[^）]+）)/);

  if (dateMatch) {
    return dateMatch[1];
  }

  return datetimeLabel.split(/\s+/)[0] || '';
};

const normalizeSeminarSlotForDetail = (slot = {}, index = 0) => {
  const title = slot.title || '';
  const partLabel = getSeminarPartLabel(index);
  const dateLabel = getSeminarSlotDateLabel(slot);
  const fallbackLabel = [dateLabel, partLabel].filter(Boolean).join(' ');

  return {
    id: slot.id,
    title,
    partLabel,
    fallbackLabel,
    eventTitle: slot.eventTitle || '',
    eventSubtitle: slot.eventSubtitle || '',
    eventId: slot.eventId || '',
    datetimeLabel: slot.datetimeLabel || '',
    locationLabel: slot.locationLabel || slot.roomName || '',
    publicLocationNote: slot.publicLocationNote || slot.publicLocation?.note || '',
    publicLocation: slot.publicLocation || null,
    capacity: slot.capacity,
    capacityLabel: slot.capacity ? `${slot.capacity}名` : '未定',
    feeLabel: slot.slotFeeLabel || '未定',
    thingsToBring: slot.thingsToBring || slot.things_to_bring || '',
    instructorName: slot.instructorName || '',
    showInstructorProfile: slot.showInstructorProfile === true,
    isOpen: slot.isOpen !== false,
    isActive: slot.isActive !== false,
    eventAcceptsApplications: slot.eventAcceptsApplications !== false,
    applicationStatus: slot.applicationStatus || (slot.isOpen !== false ? 'open' : 'closed'),
    applicationStatusLabel: slot.applicationStatusLabel || (slot.isOpen !== false ? '受付中' : '受付停止中'),
    canApply: slot.canApply === undefined ? slot.isOpen !== false : slot.canApply === true,
    room_name: slot.roomName,
  };
};

const hasUnavailableFullParticipationSlots = (section = {}) => (
  (section.fullParticipationUnavailableSlotIds || []).length > 0
);

const canUseSeminarSectionFullParticipation = (section = {}) => {
  if (hasUnavailableFullParticipationSlots(section)) {
    return false;
  }

  const slots = section.slots || [];
  const hasFullParticipationFee = Boolean(section.fullParticipationFeeLabel && section.fullParticipationFeeLabel !== '未定');
  const allVisibleSlotsCanApply = slots.length > 1 && slots.every((slot) => slot.isActive !== false && slot.canApply === true);

  if (section.acceptsApplications === false || !hasFullParticipationFee) {
    return false;
  }

  if (slots.length > 1 && !allVisibleSlotsCanApply) {
    return false;
  }

  return section.canApplyFullParticipation === true || (
    hasFullParticipationFee &&
    allVisibleSlotsCanApply
  );
};

const hasUnavailableFullSeriesParticipationSlots = (record = {}) => {
  const source = record || {};

  return (
    (
      source.seriesFullParticipationUnavailableSlotIds ||
      source.fullParticipationUnavailableSlotIds ||
    []
    ).length > 0
  );
};

const getSeminarSeriesParticipationSections = (record = {}) => (
  (record || {}).seminarSections?.length
    ? (record || {}).seminarSections
    : (record || {}).events || []
);

const getVisibleSeminarSeriesSlotIds = (record = {}) => {
  const sections = getSeminarSeriesParticipationSections(record);

  return new Set(
    sections
      .flatMap((section) => section.slots || [])
      .map((slot) => slot.id)
      .filter(Boolean),
  );
};

const getSeminarSeriesFullOption = (record = {}) => (
  (record || {}).applicationOptions?.series_full ||
  (record || {}).applicationOptions?.seriesFull ||
  {}
);

const getCoveredFullSeriesParticipationSlotIds = (record = {}) => {
  const option = getSeminarSeriesFullOption(record);

  return Array.isArray(option.covered_slot_ids)
    ? option.covered_slot_ids
    : Array.isArray(option.coveredSlotIds)
      ? option.coveredSlotIds
      : [];
};

const hasHiddenCoveredFullSeriesParticipationSlots = (record = {}) => {
  const coveredSlotIds = getCoveredFullSeriesParticipationSlotIds(record);

  if (!coveredSlotIds.length) {
    return false;
  }

  const visibleSlotIds = getVisibleSeminarSeriesSlotIds(record);

  return coveredSlotIds.some((slotId) => !visibleSlotIds.has(slotId));
};

const hasHiddenUnavailableFullSeriesParticipationSlots = (record = {}) => {
  const source = record || {};
  const unavailableSlotIds = source.seriesFullParticipationUnavailableSlotIds ||
    source.fullParticipationUnavailableSlotIds ||
    [];

  if (!unavailableSlotIds.length) {
    return false;
  }

  const visibleSlotIds = getVisibleSeminarSeriesSlotIds(record);

  return unavailableSlotIds.some((slotId) => !visibleSlotIds.has(slotId));
};

const hasUnavailableFullSeriesParticipationSections = (record = {}) => {
  const source = record || {};
  const sections = getSeminarSeriesParticipationSections(record);
  const expectedSectionsCount = Number(source.seminarsCount ?? source.eventsCount ?? sections.length);

  if (expectedSectionsCount > sections.length) {
    return true;
  }

  return sections.some((section) => section.isActive === false || section.isPublished === false);
};

const canUseSeminarSeriesFullParticipation = (record = {}) => {
  const source = record || {};

  if (
    hasUnavailableFullSeriesParticipationSlots(source) ||
    hasHiddenCoveredFullSeriesParticipationSlots(source) ||
    hasUnavailableFullSeriesParticipationSections(source)
  ) {
    return false;
  }

  const hasFullParticipationFee = Boolean(
    (source.seriesFullParticipationFeeLabel || source.fullParticipationFeeLabel) &&
    (source.seriesFullParticipationFeeLabel || source.fullParticipationFeeLabel) !== '未定',
  );
  const sections = getSeminarSeriesParticipationSections(source);
  const slots = sections.flatMap((section) => section.slots || []);
  const allVisibleSlotsCanApply = slots.length > 1 && slots.every((slot) => slot.isActive !== false && slot.canApply === true);

  if (source.acceptsApplications === false || !hasFullParticipationFee) {
    return false;
  }

  if (slots.length > 1 && !allVisibleSlotsCanApply) {
    return false;
  }

  return source.canApplyFullSeriesParticipation === true || (
    hasFullParticipationFee &&
    allVisibleSlotsCanApply
  );
};

const getDefaultSeminarSectionMode = (section = {}, isGroupedSeries = false) => {
  if (isGroupedSeries) {
    return 'none';
  }

  if (canUseSeminarSectionFullParticipation(section) && (section.slots || []).length > 1) {
    return 'event';
  }

  return 'slots';
};

const getDefaultSeminarRootParticipationMode = (record = {}) => (
  record?.seriesKind === 'grouped' && canUseSeminarSeriesFullParticipation(record) ? 'series' : 'custom'
);

const buildInitialSeminarSectionModes = (sections = [], isGroupedSeries = false) => (
  sections.reduce((acc, section) => ({
    ...acc,
    [section.id]: getDefaultSeminarSectionMode(section, isGroupedSeries),
  }), {})
);

const buildInitialSeminarSlotSelections = (sections = []) => (
  sections.reduce((acc, section) => ({
    ...acc,
    [section.id]: [],
  }), {})
);

const buildSeminarFeeItems = (record = {}, seminarSections = []) => {
  const feeItems = [];
  const addFeeItem = ({ id, title, feeLabel, scopeLabel, showFullParticipationUnavailableNotice = false }) => {
    if (!feeLabel || feeLabel === '未定') {
      return;
    }

    const duplicate = feeItems.some((item) => item.title === title && item.feeLabel === feeLabel);

    if (!duplicate) {
      feeItems.push({
        id: id || `${title}-${feeLabel}`,
        title: title || '通し参加',
        feeLabel,
        scopeLabel,
        showFullParticipationUnavailableNotice,
      });
    }
  };

  if (record.seriesKind === 'grouped') {
    const seriesHasUnavailableFullParticipationSlots =
      hasUnavailableFullSeriesParticipationSlots(record) ||
      seminarSections.some(hasUnavailableFullParticipationSlots);
    const seriesHasHiddenUnavailableFullParticipationSlots =
      hasHiddenUnavailableFullSeriesParticipationSlots({
        ...record,
        seminarSections,
      }) ||
      hasHiddenCoveredFullSeriesParticipationSlots({
        ...record,
        seminarSections,
      });
    const seriesHasUnavailableFullParticipationSections =
      hasUnavailableFullSeriesParticipationSections({
        ...record,
        seminarSections,
      });

    if (!seriesHasUnavailableFullParticipationSections && !seriesHasHiddenUnavailableFullParticipationSlots) {
      addFeeItem({
        id: record.id ? `${record.id}-series-fee` : 'series-fee',
        title: record.series?.title || record.title || '通し参加',
        feeLabel: record.seriesFullParticipationFeeLabel || record.fullParticipationFeeLabel,
        scopeLabel: 'シリーズ',
        showFullParticipationUnavailableNotice: seriesHasUnavailableFullParticipationSlots,
      });
    }

    seminarSections.forEach((section) => {
      addFeeItem({
        id: section.id ? `${section.id}-seminar-fee` : `${section.title}-seminar-fee`,
        title: section.title || 'セミナー',
        feeLabel: section.fullParticipationFeeLabel,
        scopeLabel: 'セミナー',
        showFullParticipationUnavailableNotice:
          !canUseSeminarSectionFullParticipation(section) &&
          hasUnavailableFullParticipationSlots(section),
      });
    });
  } else {
    addFeeItem({
      id: record.id ? `${record.id}-seminar-fee` : 'seminar-fee',
      title: record.title || '通し参加',
      feeLabel: record.seminarFullParticipationFeeLabel || record.fullParticipationFeeLabel,
      scopeLabel: 'セミナー',
      showFullParticipationUnavailableNotice:
        !canUseSeminarSectionFullParticipation(record) &&
        hasUnavailableFullParticipationSlots(record),
    });

    if (!feeItems.length) {
      addFeeItem({
        id: record.id ? `${record.id}-series-fee` : 'series-fee',
        title: record.series?.title || record.title || '通し参加',
        feeLabel: record.seriesFullParticipationFeeLabel,
        scopeLabel: 'シリーズ',
        showFullParticipationUnavailableNotice:
          !canUseSeminarSeriesFullParticipation(record) &&
          hasUnavailableFullSeriesParticipationSlots(record),
      });
    }
  }

  return feeItems;
};


const getFieldErrorMessage = (value) => {
  if (Array.isArray(value)) {
    return value.join(' ');
  }

  return typeof value === 'string' ? value : '';
};

const classApplicationConflictMessages = {
  inactive_schedule: '選択した開催情報は現在受付できません。',
  class_not_accepting_applications: 'この教室は現在お申込みを受け付けていません。',
  duplicate_applicant: '同じメールアドレスで既にお申込み済みです。',
  duplicate_application: '同じメールアドレスで既にお申込み済みです。',
};

const seminarApplicationConflictMessages = {
  closed_event_slot: '選択した講座枠は現在受付できません。',
  ended_event_slot: '選択した講座枠は開催終了のためお申込みできません。',
  full_event_slot: '選択した講座枠は満席のためお申込みできません。',
  full_participation_unavailable: '受付停止中・満席・開催終了の回が含まれるため、通し申込はできません。参加可能な講座枠を選択してください。',
  duplicate_applicant: '同じメールアドレスで既にお申込み済みです。',
  duplicate_application: '同じメールアドレスで既にお申込み済みです。',
};

const fullParticipationUnavailableNotice = '※現在一部の予定が参加申込受付停止中で通し参加でのお申し込みはできません。';

const getClassApplicationErrorMessage = (responseData = {}, fallbackMessage = '') => {
  const reason = responseData.reason || responseData.detail?.reason || '';

  if (reason && classApplicationConflictMessages[reason]) {
    return classApplicationConflictMessages[reason];
  }

  return getYowanRecaptchaResponseErrorMessage(responseData) ||
    (typeof responseData.detail === 'string' ? responseData.detail : '') ||
    fallbackMessage ||
    'お申込みを送信できませんでした。入力内容を確認してください。';
};

const getSeminarApplicationErrorMessage = (responseData = {}, fallbackMessage = '') => {
  const reason = responseData.reason || responseData.detail?.reason || '';

  if (reason && seminarApplicationConflictMessages[reason]) {
    return seminarApplicationConflictMessages[reason];
  }

  return getYowanRecaptchaResponseErrorMessage(responseData) ||
    (typeof responseData.detail === 'string' ? responseData.detail : '') ||
    fallbackMessage ||
    'お申込みを送信できませんでした。入力内容を確認してください。';
};

const buildClassApplicationRecaptchaErrorMessage = (error) => {
  const baseMessage = getYowanRecaptchaErrorMessage(error?.code);
  const diagnosticMessage = getYowanRecaptchaDiagnosticMessage(error);

  if (!baseMessage) {
    return '';
  }

  return diagnosticMessage ? `${baseMessage} ${diagnosticMessage}` : baseMessage;
};

const normalizeYowanAccessValues = (...values) => (
  values.flatMap((value) => {
    if (!value) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  })
);

const hasYowanMemberAccessSignal = (value = '') => {
  const normalizedValue = String(value || '').trim().toLowerCase();

  return normalizedValue === 'member' ||
    normalizedValue === 'yowan_member' ||
    normalizedValue.includes('member') ||
    normalizedValue.includes('会員') ||
    normalizedValue.includes('一般会員');
};

const hasYowanMemberAccess = (accessMe = {}) => {
  const memberAccess = accessMe.member_access || accessMe.memberAccess || null;

  if (Array.isArray(memberAccess)) {
    return memberAccess.length > 0;
  }

  if (memberAccess && typeof memberAccess === 'object') {
    return Boolean(
      memberAccess.id ||
      memberAccess.member_id ||
      memberAccess.memberId ||
      memberAccess.lifecycle_status ||
      memberAccess.lifecycleStatus ||
      memberAccess.status ||
      memberAccess.is_member === true ||
      memberAccess.isMember === true
    );
  }

  const memberAccessSignals = normalizeYowanAccessValues(
    accessMe.access_labels,
    accessMe.accessLabels,
    accessMe.qualification_keys,
    accessMe.qualificationKeys,
    accessMe.qualification_labels,
    accessMe.qualificationLabels,
    accessMe.member_qualification_keys,
    accessMe.memberQualificationKeys,
    accessMe.member_qualification_labels,
    accessMe.memberQualificationLabels,
    accessMe.member_access_kinds,
    accessMe.memberAccessKinds,
    accessMe.member_access_keys,
    accessMe.memberAccessKeys,
    accessMe.member_access_labels,
    accessMe.memberAccessLabels
  );

  if (memberAccessSignals.some(hasYowanMemberAccessSignal)) {
    return true;
  }

  return Boolean(
    accessMe.has_member_access === true ||
    accessMe.hasMemberAccess === true ||
    accessMe.is_member === true ||
    accessMe.isMember === true
  );
};

const shouldPrefillYowanMemberApplicationForm = async ({ accessMe = {}, profile = {} }) => {
  if (hasYowanMemberAccess(accessMe)) {
    return true;
  }

  const canAccessAdmin = accessMe.can_access_admin === true || accessMe.canAccessAdmin === true;

  if (!canAccessAdmin) {
    return Boolean(profile.id);
  }

  const email = profile.email || '';

  if (!email) {
    return false;
  }

  const memberAccessList = await fetchYowanAdminMemberAccessList({
    page: 1,
    pageSize: 5,
    q: email,
  });

  return memberAccessList.results.some((item) => (
    item.memberId === profile.id ||
    item.accountEmail === email ||
    item.member?.email === email
  ));
};

const applyMemberProfileDefaultsToForm = (profile = {}, { setValue }) => {
  const defaults = {
    name: profile.name || '',
    name_kana: profile.nameKana || '',
    email: profile.email || '',
    phone: profile.phone || '',
  };
  const appliedFields = {};

  Object.entries(defaults).forEach(([fieldName, value]) => {
    if (!value) {
      return;
    }

    setValue(fieldName, value, { shouldDirty: false, shouldValidate: false });
    appliedFields[fieldName] = true;
  });

  return appliedFields;
};

const seminarMemberReadOnlyControlStyle = { cursor: 'not-allowed' };

const buildApiDetailContent = (record, isSeminarDetail, fallbackContent) => {
  if (!record) {
    return null;
  }

  const primarySlot = isSeminarDetail ? pickFirstActiveItem(record.slots) : pickFirstActiveItem(record.schedules);
  const scheduleItems = isSeminarDetail ? [] : (record.schedules || []).map(formatClassScheduleItem);
  const publicInstructors = record.publicInstructors || [];
  const detailTopImage = isSeminarDetail
    ? (record.primary_image || record.primaryImage || null)
    : (record.detail_top_image || record.detailTopImage || null);
  const detailTopImageSrc = detailTopImage?.preview_url || detailTopImage?.previewUrl || detailTopImage?.url || '';
  const hasMultipleClassSchedules = scheduleItems.length > 1;
  const guidanceParagraphs = splitTextParagraphs(record.guidance || (!isSeminarDetail ? record.summary : '') || '');
  const fallbackForm = fallbackContent?.form || defaultDetailFormContent;
  const seminarBody = {
    datetime: primarySlot?.datetimeLabel || '開催日時は調整中です。',
    location: primarySlot?.locationLabel || primarySlot?.roomName || primarySlot?.address || '会場は調整中です。',
    capacity: primarySlot?.capacity ? `${primarySlot.capacity}名` : '未定',
    fee: primarySlot?.feeLabel || primarySlot?.slotFeeLabel || '未定',
    instructor: primarySlot?.instructorName || record.standardInstructor || '-',
  };
  const classBody = {
    datetime: hasMultipleClassSchedules
      ? `${scheduleItems.length}件の開催情報`
      : primarySlot?.datetimeLabel || '開催日時は調整中です。',
    location: hasMultipleClassSchedules
      ? '各開催情報をご確認ください。'
      : getClassScheduleLocationText(primarySlot),
    capacity: hasMultipleClassSchedules ? '開催情報ごとに設定' : primarySlot?.capacity ? `${primarySlot.capacity}名` : '未定',
    fee: hasMultipleClassSchedules ? '開催情報ごとに設定' : primarySlot?.feeLabel || '未定',
    instructor: hasMultipleClassSchedules ? '開催情報ごとに設定' : primarySlot?.instructorName || record.standardInstructor || '-',
  };
  const bodySummary = isSeminarDetail ? seminarBody : classBody;
  const seminarSectionSourceEvents = new Map((record.events || []).map((eventItem) => [eventItem.id, eventItem]));
  const seminarSections = isSeminarDetail
    ? (
        record.seminarSections?.length
          ? record.seminarSections
          : [{ id: record.id, title: record.title, subtitle: record.subtitle, slots: record.slots || [] }]
      ).map((section, sectionIndex) => {
        const sourceEvent = seminarSectionSourceEvents.get(section.id) || section;
        const sectionImage = sourceEvent.primary_image || sourceEvent.primaryImage || null;
        const sectionImageSrc = sectionImage?.previewUrl || sectionImage?.preview_url || sectionImage?.url || sourceEvent.image || '';

        return {
          id: section.id || `seminar-section-${sectionIndex}`,
          title: section.title || '',
          subtitle: section.subtitle || '',
          isActive: sourceEvent.isActive !== false && section.isActive !== false,
          isPublished: sourceEvent.isPublished !== false && section.isPublished !== false,
          acceptsApplications: sourceEvent.acceptsApplications !== false && section.acceptsApplications !== false,
          canApplyFullParticipation: section.canApplyFullParticipation === undefined
            ? sourceEvent.canApplyFullParticipation
            : section.canApplyFullParticipation,
          fullParticipationUnavailableSlotIds: Array.isArray(section.fullParticipationUnavailableSlotIds)
            ? section.fullParticipationUnavailableSlotIds
            : Array.isArray(sourceEvent.fullParticipationUnavailableSlotIds)
              ? sourceEvent.fullParticipationUnavailableSlotIds
              : [],
          fullParticipationFeeLabel: section.fullParticipationFeeLabel || '',
          guidance: splitTextParagraphs(sourceEvent.guidance || section.guidance || ''),
          image: sectionImageSrc
            ? {
                src: sectionImageSrc,
                alt: sectionImage?.altText || sectionImage?.alt_text || sourceEvent.title || section.title || record.title,
              }
            : null,
          slots: (section.slots || []).map((slot, index) => normalizeSeminarSlotForDetail(slot, index)),
        };
      })
    : [];
  const seminarFeeItems = isSeminarDetail ? buildSeminarFeeItems(record, seminarSections) : [];

  return {
    hero: {
      images: detailTopImageSrc
        ? [
            {
              src: detailTopImageSrc,
              alt: detailTopImage.alt_text || detailTopImage.altText || record.title,
              label: detailTopImage.alt_text || detailTopImage.altText || record.title,
              caption: detailTopImage.caption || '',
            },
          ]
        : [],
    },
    calendarUrl: record.calendarUrl || '',
    body: {
      title: record.title || '',
      subtitle: record.subtitle || '',
      datetime: bodySummary.datetime,
      location: bodySummary.location,
      capacity: bodySummary.capacity,
      fee: bodySummary.fee,
      instructor: bodySummary.instructor,
      guidance: guidanceParagraphs.length ? guidanceParagraphs : ['詳細は準備中です。'],
    },
    scheduleItems,
    publicInstructors,
    slots: isSeminarDetail
      ? (record.slots || []).map((slot, index) => normalizeSeminarSlotForDetail(slot, index))
      : [],
    seminarSections,
    seminarFeeItems,
    form: fallbackForm,
  };
};

const EventDetailPage = () => {
  const location = useLocation();
  const { eventId } = useParams();
  const { pathname } = location;
  const metadata = getYowanRouteMeta(pathname);
  const isSeminarDetail = pathname.startsWith('/seminars/');
  const dynamicDetailId = eventId;
  const [apiRecord, setApiRecord] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(Boolean(dynamicDetailId));
  const [detailLoadError, setDetailLoadError] = useState('');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [seminarParticipationMode, setSeminarParticipationMode] = useState('series');
  const [seminarSectionModes, setSeminarSectionModes] = useState({});
  const [seminarSlotSelections, setSeminarSlotSelections] = useState({});
  const [seminarMemberReadOnlyFields, setSeminarMemberReadOnlyFields] = useState({});
  const content = useMemo(
    () => buildApiDetailContent(apiRecord, isSeminarDetail, null),
    [apiRecord, isSeminarDetail],
  );
  const images = content?.hero?.images || [];
  const activeImage = images[activeImageIndex] || null;
  const seminarSections = useMemo(() => content?.seminarSections || [], [content]);
  const seminarFeeItems = content?.seminarFeeItems || [];
  const visibleSeminarSections = useMemo(
    () => (
      isSeminarDetail
        ? seminarSections
          .map((section) => ({
            ...section,
            slots: (section.slots || []).filter((slot) => slot.isActive !== false),
          }))
          .filter((section) => section.slots.length || section.title || section.subtitle)
        : []
    ),
    [isSeminarDetail, seminarSections],
  );
  const visibleSeminarSectionKey = visibleSeminarSections
    .map((section) => `${section.id}:${section.slots?.length || 0}`)
    .join('|');
  const isGroupedApiSeminarSeries = apiRecord?.seriesKind === 'grouped';
  const canUseSeriesFullParticipation = useMemo(
    () => canUseSeminarSeriesFullParticipation(apiRecord),
    [apiRecord],
  );
  const scheduleItems = content?.scheduleItems || [];
  const publicInstructors = content?.publicInstructors || [];
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      name_kana: '',
      email: '',
      gender: '',
      age_range: '',
      preferred_slot: '',
      preferred_schedule: '',
      preferred_date: '',
      phone: '',
      message: '',
    },
  });

  useEffect(() => {
    if (!isSeminarDetail) {
      return;
    }

    setSeminarParticipationMode(
      isGroupedApiSeminarSeries && canUseSeriesFullParticipation
        ? 'series'
        : 'custom',
    );
    setSeminarSectionModes(buildInitialSeminarSectionModes(
      visibleSeminarSections,
      isGroupedApiSeminarSeries,
    ));
    setSeminarSlotSelections(buildInitialSeminarSlotSelections(visibleSeminarSections));
    clearErrors('preferred_slot');
  }, [
    isSeminarDetail,
    apiRecord?.id,
    isGroupedApiSeminarSeries,
    canUseSeriesFullParticipation,
    visibleSeminarSectionKey,
    visibleSeminarSections,
    clearErrors,
  ]);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [pathname]);

  useEffect(() => {
    if (!isSeminarDetail || !formOpen) {
      return undefined;
    }

    let isMounted = true;

    setSeminarMemberReadOnlyFields({});

    Promise.all([
      fetchYowanAccessMe(),
      fetchYowanCurrentMemberProfile(),
    ])
      .then(async ([accessMe, profile]) => {
        if (!isMounted) {
          return;
        }

        const shouldPrefill = await shouldPrefillYowanMemberApplicationForm({ accessMe, profile });

        if (!isMounted || !shouldPrefill) {
          return;
        }

        setSeminarMemberReadOnlyFields(
          applyMemberProfileDefaultsToForm(profile, { setValue }),
        );
      })
      .catch(() => {
        // 未ログイン、Member未作成、Operator-only は空フォームのまま扱う。
      });

    return () => {
      isMounted = false;
    };
  }, [formOpen, isSeminarDetail, pathname, setValue]);

  useEffect(() => {
    if (!formOpen || !isSeminarDetail) {
      setSeminarMemberReadOnlyFields({});
    }
  }, [formOpen, isSeminarDetail, pathname]);

  useEffect(() => {
    let isMounted = true;

    if (!dynamicDetailId) {
      setApiRecord(null);
      setIsLoadingDetail(false);
      setDetailLoadError('');
      return () => {
        isMounted = false;
      };
    }

    setIsLoadingDetail(true);
    setDetailLoadError('');

    const request = isSeminarDetail
      ? fetchYowanPublicSeminarDetail(dynamicDetailId)
      : fetchYowanPublicClassDetail(dynamicDetailId);

    request
      .then((record) => {
        if (!isMounted) {
          return;
        }

        setApiRecord(record);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setApiRecord(null);
        setDetailLoadError('教室・講座情報を読み込めませんでした。');
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingDetail(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [dynamicDetailId, isSeminarDetail]);

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  const isGroupedSeminarSeries = isSeminarDetail && apiRecord?.seriesKind === 'grouped';
  const classApplicationOptions = !isSeminarDetail
    ? scheduleItems.length
      ? scheduleItems.filter((schedule) => schedule.isActive && schedule.acceptsApplications)
      : [buildFallbackClassScheduleItem(content)]
    : [];
  const singleClassApplicationScheduleId = !isSeminarDetail && classApplicationOptions.length === 1
    ? classApplicationOptions[0]?.id || ''
    : '';

  useEffect(() => {
    if (isSeminarDetail) {
      return;
    }

    if (singleClassApplicationScheduleId && singleClassApplicationScheduleId !== 'class-schedule-default') {
      setValue('preferred_schedule', singleClassApplicationScheduleId, {
        shouldDirty: false,
        shouldValidate: false,
      });
      clearErrors('preferred_schedule');
      return;
    }

    setValue('preferred_schedule', '', {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [clearErrors, isSeminarDetail, setValue, singleClassApplicationScheduleId]);

  useEffect(() => {
    if (!apiRecord?.id || !content) {
      return;
    }

    const title = content.body?.title || apiRecord.title || metadata?.title || '揺腕法';
    const description = content.body?.subtitle ||
      apiRecord.summary ||
      apiRecord.guidance ||
      metadata?.description ||
      title;
    const canonicalPath = apiRecord.publicPath ||
      (isSeminarDetail
        ? `/seminars/${apiRecord.publicSlug || apiRecord.id}`
        : `/classes/${apiRecord.publicSlug || apiRecord.id}`);
    const image = activeImage?.src ||
      apiRecord.primary_image?.preview_url ||
      apiRecord.primary_image?.url ||
      apiRecord.image ||
      '';

    applyYowanDetailMetadata({
      title,
      description,
      canonicalPath,
      image,
      type: isSeminarDetail ? 'event' : 'website',
    });
  }, [activeImage?.src, apiRecord, content, isSeminarDetail, metadata]);

  const seminarSelectionSections = isSeminarDetail
    ? visibleSeminarSections
      .map((section) => ({
        ...section,
        slots: (section.slots || []).filter((slot) => slot.id && slot.isActive && slot.canApply),
      }))
      .filter((section) => section.id && section.slots.length)
    : [];
  const seminarApplicationOptions = seminarSelectionSections.flatMap((section) => section.slots);

  const setSeminarSectionMode = (sectionId, mode) => {
    setSeminarSectionModes((current) => ({
      ...current,
      [sectionId]: mode,
    }));

    if (mode !== 'slots') {
      setSeminarSlotSelections((current) => ({
        ...current,
        [sectionId]: [],
      }));
    }

    clearErrors('preferred_slot');
  };

  const setSeminarRootParticipationMode = (mode) => {
    setSeminarParticipationMode(mode);

    if (mode === 'series') {
      setSeminarSectionModes(buildInitialSeminarSectionModes(seminarSelectionSections, true));
      setSeminarSlotSelections(buildInitialSeminarSlotSelections(seminarSelectionSections));
    }

    clearErrors('preferred_slot');
  };

  const toggleSeminarSlotSelection = (sectionId, slotId) => {
    setSeminarSlotSelections((current) => {
      const currentSlotIds = current[sectionId] || [];
      const nextSlotIds = currentSlotIds.includes(slotId)
        ? currentSlotIds.filter((id) => id !== slotId)
        : [...currentSlotIds, slotId];

      return {
        ...current,
        [sectionId]: nextSlotIds,
      };
    });
    clearErrors('preferred_slot');
  };

  const buildSeminarApplicationSelectionPayload = () => {
    if (!apiRecord?.id) {
      return { errorMessage: content?.form?.pendingMessage || '送信処理を受け付けました。' };
    }

    if (isGroupedSeminarSeries && seminarParticipationMode === 'series') {
      if (!canUseSeminarSeriesFullParticipation(apiRecord)) {
        return { errorMessage: seminarApplicationConflictMessages.full_participation_unavailable };
      }

      return {
        participationScope: 'series',
        eventSelections: undefined,
      };
    }

    const eventSelections = [];
    const emptySlotSelection = seminarSelectionSections.some((section) => {
      const mode = seminarSectionModes[section.id] || getDefaultSeminarSectionMode(section, isGroupedSeminarSeries);

      if (mode === 'none') {
        return false;
      }

      if (mode === 'event') {
        if (!canUseSeminarSectionFullParticipation(section)) {
          return true;
        }

        eventSelections.push({
          event_id: section.id,
          scope: 'event',
        });
        return false;
      }

      const slotIds = (seminarSlotSelections[section.id] || [])
        .filter((slotId) => section.slots.some((slot) => slot.id === slotId));

      if (!slotIds.length) {
        return true;
      }

      if (
        canUseSeminarSectionFullParticipation(section) &&
        section.slots.length > 1 &&
        slotIds.length === section.slots.length
      ) {
        eventSelections.push({
          event_id: section.id,
          scope: 'event',
        });
        return false;
      }

      eventSelections.push({
        event_id: section.id,
        scope: 'slots',
        slot_ids: slotIds,
      });
      return false;
    });

    if (emptySlotSelection) {
      return { errorMessage: '個別に参加する講座枠を選択してください。' };
    }

    if (!eventSelections.length) {
      return { errorMessage: '参加するセミナーまたは講座枠を選択してください。' };
    }

    const coversAllSeminarsAsFull = isGroupedSeminarSeries &&
      canUseSeminarSeriesFullParticipation(apiRecord) &&
      eventSelections.length === seminarSelectionSections.length &&
      eventSelections.every((selection) => selection.scope === 'event');

    if (coversAllSeminarsAsFull) {
      return {
        participationScope: 'series',
        eventSelections: undefined,
      };
    }

    return {
      participationScope: 'mixed',
      eventSelections,
    };
  };

  if (isLoadingDetail) {
    return (
      <Section>
        <PageContainer>
          <LoadingSpinner label="教室・講座情報を読み込み中" showLabel />
        </PageContainer>
      </Section>
    );
  }

  if (detailLoadError) {
    return (
      <Section>
        <PageContainer>
          <p className="yowan-event-detail-form__message">{detailLoadError}</p>
        </PageContainer>
      </Section>
    );
  }

  if (!content) {
    return null;
  }

  const hasMultipleImages = images.length > 1;
  const shouldShowTopHero = images.length > 0 && (!isSeminarDetail || isGroupedSeminarSeries);
  const shouldOverlapSeriesHeader = shouldShowTopHero && isGroupedSeminarSeries;
  const contentClassName = [
    'yowan-event-detail-content',
    isSeminarDetail ? 'yowan-event-detail-content--seminar' : '',
    shouldOverlapSeriesHeader ? 'yowan-event-detail-content--series-hero-overlap' : '',
  ].filter(Boolean).join(' ');
  const applicationMessageMaxLength = Number(content.form.messageMaxLength || APPLICATION_MESSAGE_MAX_LENGTH);
  const applicationSubmittingMessage = content.form.submittingMessage ||
    '申込を送信しています。画面を閉じずにお待ちください。';

  const moveImage = (direction) => {
    if (!images.length) {
      return;
    }

    setActiveImageIndex((prev) => (prev + direction + images.length) % images.length);
  };

  const onSubmit = async (values) => {
    if (!apiRecord?.id) {
      setSubmitMessage(content.form.pendingMessage);
      return;
    }

    if (isSeminarDetail) {
      const seminarSelectionPayload = buildSeminarApplicationSelectionPayload();

      if (seminarSelectionPayload.errorMessage) {
        setError('preferred_slot', { type: 'manual', message: seminarSelectionPayload.errorMessage });
        setSubmitMessage(seminarSelectionPayload.errorMessage);
        return;
      }

      setIsSubmitting(true);
      setSubmitMessage('送信中です。');

      try {
        const recaptchaToken = await executeYowanRecaptcha(
          YOWAN_RECAPTCHA_ACTIONS.seminarApplication,
        );

        await submitYowanPublicSeminarSeriesApplication(apiRecord.id, {
          ...values,
          recaptcha_token: recaptchaToken,
          ...seminarSelectionPayload,
        });
        reset();
        setSeminarMemberReadOnlyFields({});
        setSeminarParticipationMode(getDefaultSeminarRootParticipationMode(apiRecord));
        setSeminarSectionModes(buildInitialSeminarSectionModes(
          visibleSeminarSections,
          apiRecord?.seriesKind === 'grouped',
        ));
        setSeminarSlotSelections(buildInitialSeminarSlotSelections(visibleSeminarSections));
        setSubmitMessage(content.form.successMessage || 'お申込みを受け付けました。');
      } catch (error) {
        const localRecaptchaMessage = buildClassApplicationRecaptchaErrorMessage(error);

        if (localRecaptchaMessage) {
          setSubmitMessage(localRecaptchaMessage);
          return;
        }

        const responseData = error?.response?.data || {};
        const fieldMappings = [
          ['name', 'name'],
          ['name_kana', 'name_kana'],
          ['email', 'email'],
          ['gender', 'gender'],
          ['age_range', 'age'],
          ['phone', 'phone'],
          ['message', 'message'],
        ];

        fieldMappings.forEach(([formFieldName, responseFieldName]) => {
          const message = getFieldErrorMessage(responseData[responseFieldName]);

          if (message) {
            setError(formFieldName, { type: 'server', message });
          }
        });

        setSubmitMessage(getSeminarApplicationErrorMessage(responseData, content.form.errorMessage));
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!values.preferred_schedule || values.preferred_schedule === 'class-schedule-default') {
      setSubmitMessage('お申込み可能な開催情報がありません。');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('送信中です。');

    try {
      const recaptchaToken = await executeYowanRecaptcha(
        YOWAN_RECAPTCHA_ACTIONS.classApplication,
      );

      await submitYowanPublicClassScheduleApplication(values.preferred_schedule, {
        ...values,
        recaptcha_token: recaptchaToken,
      });
      reset();
      if (singleClassApplicationScheduleId && singleClassApplicationScheduleId !== 'class-schedule-default') {
        setValue('preferred_schedule', singleClassApplicationScheduleId, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      setSubmitMessage(content.form.successMessage || 'お申込みを受け付けました。');
    } catch (error) {
      const localRecaptchaMessage = buildClassApplicationRecaptchaErrorMessage(error);

      if (localRecaptchaMessage) {
        setSubmitMessage(localRecaptchaMessage);
        return;
      }

      const responseData = error?.response?.data || {};
      const fieldMappings = [
        ['name', 'name'],
        ['name_kana', 'name_kana'],
        ['email', 'email'],
        ['phone', 'phone'],
        ['message', 'message'],
        ['preferred_date', 'desired_date'],
      ];

      fieldMappings.forEach(([formFieldName, responseFieldName]) => {
        const message = getFieldErrorMessage(responseData[responseFieldName]);

        if (message) {
          setError(formFieldName, { type: 'server', message });
        }
      });

      setSubmitMessage(getClassApplicationErrorMessage(responseData, content.form.errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmitClassApplication = isSeminarDetail ||
    !apiRecord?.id ||
    (
      apiRecord.acceptsApplications !== false &&
      classApplicationOptions.some((schedule) => schedule.id && schedule.id !== 'class-schedule-default')
    );
  const canSubmitApplication = isSeminarDetail
    ? seminarApplicationOptions.length > 0
    : canSubmitClassApplication;
  const stoppedSeminarSections = isSeminarDetail
    ? visibleSeminarSections.filter((section) => section.acceptsApplications === false)
    : [];
  const applicationUnavailableMessage = isSeminarDetail && stoppedSeminarSections.length && !seminarApplicationOptions.length
    ? '現在、このセミナーの申込受付は停止しています。'
    : '現在お申込み可能な開催情報はありません。';
  const renderSeminarSectionHeader = (section) => (
    section.title || section.subtitle ? (
      <header className="yowan-event-detail-seminar-section__header">
        {section.title ? (
          <h3 className="yowan-event-detail-seminar-section__title">{section.title}</h3>
        ) : null}
        {section.subtitle ? (
          <p className="yowan-event-detail-seminar-section__subtitle">{section.subtitle}</p>
        ) : null}
      </header>
    ) : null
  );
  const renderSeminarSectionSlots = (section) => (
    section.slots.length ? (
      <div className="yowan-event-detail-seminar-slots">
        {section.acceptsApplications === false ? (
          <p className="yowan-event-detail-form__message">
            このセミナーは現在、申込受付を停止しています。開催情報は確認できますが、新規申込はできません。
          </p>
        ) : null}
        {section.slots.map((slot, index) => {
          const locationNote = slot.publicLocationNote || '';
          const status = slot.applicationStatus || (slot.isOpen !== false ? 'open' : 'closed');
          const statusLabel = slot.eventAcceptsApplications === false && status === 'closed'
            ? 'セミナー受付停止中'
            : slot.applicationStatusLabel || (slot.isOpen !== false ? '受付中' : '受付停止中');

          return (
            <article key={slot.id || `${slot.title}-${index}`} className="yowan-event-detail-seminar-slots__card">
              <p className="yowan-event-detail-seminar-slots__eyebrow">
                <span className={`yowan-event-detail-seminar-slots__status yowan-event-detail-seminar-slots__status--${status}`}>
                  {statusLabel}
                </span>
                {section.slots.length > 1 && !slot.title && slot.partLabel ? ` / ${slot.partLabel}` : ''}
              </p>
              {slot.title ? (
                <h3 className="yowan-event-detail-seminar-slots__title">
                  {slot.title}
                </h3>
              ) : null}
              <dl className="yowan-event-detail-content__definition yowan-event-detail-seminar-slots__definition">
                <div>
                  <dt>日時</dt>
                  <dd>{slot.datetimeLabel || '開催日時は調整中です。'}</dd>
                </div>
                <div>
                  <dt>場所</dt>
                  <dd>
                    <span>{slot.locationLabel || '会場は調整中です。'}</span>
                    {locationNote ? (
                      <small className="yowan-event-detail-seminar-slots__location-note">{locationNote}</small>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt>定員</dt>
                  <dd>{slot.capacityLabel || (slot.capacity ? `${slot.capacity}名` : '未定')}</dd>
                </div>
                <div>
                  <dt>費用</dt>
                  <dd>{slot.feeLabel || '未定'}</dd>
                </div>
                {slot.thingsToBring ? (
                  <div>
                    <dt>持ち物</dt>
                    <dd>{slot.thingsToBring}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>講師名</dt>
                  <dd>{slot.instructorName || '-'}</dd>
                </div>
              </dl>
            </article>
          );
        })}
      </div>
    ) : null
  );
  const renderSeminarSectionFee = (section) => (
    section.fullParticipationFeeLabel && section.fullParticipationFeeLabel !== '未定' ? (
      <section className="yowan-event-detail-seminar-fees" aria-label={`${section.title || 'セミナー'}の通し参加費`}>
        <h3 className="yowan-event-detail-seminar-fees__title">通し参加費</h3>
        <div className="yowan-event-detail-seminar-fees__list">
          <div className="yowan-event-detail-seminar-fees__item">
            <div className="yowan-event-detail-seminar-fees__body">
              <span className="yowan-event-detail-seminar-fees__scope">セミナー</span>
              <span className="yowan-event-detail-seminar-fees__name">{section.title || content.body.title}</span>
            </div>
            <span className="yowan-event-detail-seminar-fees__price">{section.fullParticipationFeeLabel}</span>
          </div>
        </div>
        {!canUseSeminarSectionFullParticipation(section) && hasUnavailableFullParticipationSlots(section) ? (
          <p className="yowan-event-detail-seminar-fees__notice">{fullParticipationUnavailableNotice}</p>
        ) : null}
      </section>
    ) : null
  );
  const renderGroupedSeriesFee = () => {
    const seriesFeeItem = seminarFeeItems.find((item) => (
      item.scopeLabel === 'シリーズ' || item.scopeLabel === '一連の講習会'
    ));

    if (!seriesFeeItem) {
      return null;
    }

    return (
      <section
        className="yowan-event-detail-seminar-fees yowan-event-detail-seminar-fees--series c-surface-content"
        aria-label="シリーズの通し参加費"
      >
        <h3 className="yowan-event-detail-seminar-fees__title">{seriesFeeItem.title} 通し参加費</h3>
        <p className="yowan-event-detail-seminar-fees__lead">
          {seriesFeeItem.title}に通しでご参加される場合の参加費です。
        </p>
        <div className="yowan-event-detail-seminar-fees__list">
          <div className="yowan-event-detail-seminar-fees__item">
            <div className="yowan-event-detail-seminar-fees__body">
              <span className="yowan-event-detail-seminar-fees__scope">シリーズ</span>
              <span className="yowan-event-detail-seminar-fees__name">{seriesFeeItem.title}</span>
            </div>
            <span className="yowan-event-detail-seminar-fees__price">{seriesFeeItem.feeLabel}</span>
          </div>
        </div>
        {seriesFeeItem.showFullParticipationUnavailableNotice ? (
          <p className="yowan-event-detail-seminar-fees__notice">{fullParticipationUnavailableNotice}</p>
        ) : null}
      </section>
    );
  };
  const renderGroupedSeminarSeries = () => (
    visibleSeminarSections.length ? (() => {
      const publicInstructorContent = renderPublicInstructors();
      const seriesFeeContent = renderGroupedSeriesFee();

      return (
        <div className="yowan-event-detail-series-layout">
          {visibleSeminarSections.map((section, sectionIndex) => {
            const guidanceParagraphsForSection = section.guidance?.length
              ? section.guidance
              : ['詳細は準備中です。'];
            const sectionImage = section.image;

            return (
              <React.Fragment key={section.id || `${section.title}-${sectionIndex}`}>
                {sectionIndex > 0 ? (
                  <div className="yowan-event-detail-seminar-section-separator" aria-hidden="true" />
                ) : null}
                <section className="yowan-event-detail-series-row">
                  <div className="yowan-event-detail-content__meta yowan-event-detail-content__meta--seminar c-surface-content">
                    <h2 className="yowan-event-detail-seminar-slots__heading">開催日程</h2>
                    <div className="yowan-event-detail-seminar-section">
                      {renderSeminarSectionHeader(section)}
                      {renderSeminarSectionSlots(section)}
                    </div>
                  </div>
                  <div className="yowan-event-detail-content__guidance">
                    {sectionImage?.src ? (
                      <div className="yowan-event-detail-content__guidance-image">
                        <img
                          className="yowan-event-detail-content__guidance-img"
                          src={sectionImage.src}
                          alt={sectionImage.alt || sectionImage.label || section.title || content.body.title}
                          loading="lazy"
                        />
                      </div>
                    ) : null}
                    <div className="yowan-event-detail-content__guidance-copy yowan-event-detail-content__guidance-copy--seminar c-surface-content">
                      {guidanceParagraphsForSection.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                      {renderSeminarSectionFee(section)}
                    </div>
                  </div>
                </section>
              </React.Fragment>
            );
          })}
          {publicInstructorContent ? (
            <div className="yowan-event-detail-series-instructors c-surface-content">
              {publicInstructorContent}
            </div>
          ) : null}
          {seriesFeeContent}
        </div>
      );
    })() : (
      <p className="yowan-event-detail-schedules__empty">開催日程は準備中です。</p>
    )
  );
  const renderPublicInstructors = () => {
    if (!publicInstructors.length) {
      return null;
    }

    return (
      <div className="yowan-event-detail-instructors">
        <h3 className="yowan-event-detail-instructors__title">講師紹介</h3>
        {publicInstructors.map((instructor) => (
          <article key={instructor.id || instructor.instructorName} className="yowan-event-detail-instructors__item">
            <div className="yowan-event-detail-instructors__photo" aria-hidden={!instructor.image}>
              {instructor.image ? (
                <img
                  className="yowan-event-detail-instructors__image"
                  src={instructor.image}
                  alt={instructor.primary_image?.alt_text || instructor.instructorName}
                  loading="lazy"
                />
              ) : null}
            </div>
            <div className="yowan-event-detail-instructors__body">
              {instructor.roleLabel ? (
                <p className="yowan-event-detail-instructors__role">{instructor.roleLabel}</p>
              ) : null}
              <h3 className="yowan-event-detail-instructors__name">{instructor.instructorName}</h3>
              {instructor.career || instructor.biography ? (
                <p className="yowan-event-detail-instructors__text">{instructor.career || instructor.biography}</p>
              ) : null}
              {instructor.webLinks?.length ? (
                <div className="yowan-event-detail-instructors__links" aria-label={`${instructor.instructorName} の外部リンク`}>
                  {instructor.webLinks.map((link) => {
                    const label = normalizeInstructorWebLinkLabel(
                      link.label || instructorWebLinkLabels[link.platformKey] || 'リンク',
                    );

                    return (
                      <a
                        key={`${link.platformKey}-${link.url}`}
                        className="yowan-event-detail-instructors__link"
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`${instructor.instructorName} の${label}を開く`}
                        title={label}
                      >
                        <InstructorWebLinkIcon platformKey={link.platformKey} />
                        <span className="u-visually-hidden">{label}</span>
                      </a>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    );
  };

  return (
    <>
      {shouldShowTopHero ? (
        <Section className={shouldOverlapSeriesHeader ? 'yowan-event-detail-hero-section--series' : ''}>
          <div className="yowan-event-detail-hero">
            <div className="yowan-event-detail-hero__media">
              <div className="yowan-event-detail-hero__image">
                {activeImage?.src ? (
                  <img
                    className="yowan-event-detail-hero__img"
                    src={activeImage.src}
                    alt={activeImage.alt || activeImage.label || content.body.title}
                  />
                ) : activeImage ? (
                  <span>{activeImage.label}</span>
                ) : null}
              </div>
              {activeImage?.caption ? (
                <p className="yowan-event-detail-hero__caption">{activeImage.caption}</p>
              ) : null}
            </div>
            {hasMultipleImages ? (
              <>
                <button
                  type="button"
                  className="yowan-event-detail-hero__nav yowan-event-detail-hero__nav--prev"
                  aria-label="前の画像へ"
                  onClick={() => moveImage(-1)}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="yowan-event-detail-hero__nav yowan-event-detail-hero__nav--next"
                  aria-label="次の画像へ"
                  onClick={() => moveImage(1)}
                >
                  ›
                </button>
              </>
            ) : null}
          </div>
        </Section>
      ) : null}

      <Section className={shouldOverlapSeriesHeader ? 'yowan-event-detail-content-section--series-hero-overlap' : ''}>
        <PageContainer>
          <div className={contentClassName}>
            <div className="yowan-event-detail-content__header">
              <Heading
                as="h1"
                className={isSeminarDetail ? 'yowan-event-detail-content__heading--seminar' : ''}
              >
                {content.body.title}
              </Heading>
              {content.body.subtitle ? (
                <p className="yowan-event-detail-content__subtitle">{content.body.subtitle}</p>
              ) : null}
            </div>

            {isSeminarDetail ? (
              isGroupedSeminarSeries ? renderGroupedSeminarSeries() : (
              <div className="yowan-event-detail-content__main">
                <div className="yowan-event-detail-content__meta yowan-event-detail-content__meta--seminar c-surface-content">
                  <h2 className="yowan-event-detail-seminar-slots__heading">開催日程</h2>
                  {visibleSeminarSections.length ? (
                    <div className="yowan-event-detail-seminar-sections">
                      {visibleSeminarSections.map((section, sectionIndex) => (
                        <section
                          key={section.id || `${section.title}-${sectionIndex}`}
                          className="yowan-event-detail-seminar-section"
                        >
                          {renderSeminarSectionHeader(section)}
                          {renderSeminarSectionSlots(section)}
                        </section>
                      ))}
                    </div>
                  ) : (
                    <p className="yowan-event-detail-schedules__empty">開催日程は準備中です。</p>
                  )}
                </div>

                <div className="yowan-event-detail-content__guidance">
                  {activeImage?.src ? (
                    <div className="yowan-event-detail-content__guidance-image">
                      <img
                        className="yowan-event-detail-content__guidance-img"
                        src={activeImage.src}
                        alt={activeImage.alt || activeImage.label || content.body.title}
                        loading="lazy"
                      />
                    </div>
                  ) : null}
                  <div className="yowan-event-detail-content__guidance-copy yowan-event-detail-content__guidance-copy--seminar c-surface-content">
                    {content.body.guidance.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                    {seminarFeeItems.length ? (
                      <section className="yowan-event-detail-seminar-fees" aria-label="通し参加費">
                        <h3 className="yowan-event-detail-seminar-fees__title">通し参加費</h3>
                        <p className="yowan-event-detail-seminar-fees__lead">
                          本セミナーに通しでご参加される方には、以下の通し参加費が適用されます。
                        </p>
                        <div className="yowan-event-detail-seminar-fees__list">
                          {seminarFeeItems.map((item) => (
                            <div key={item.id} className="yowan-event-detail-seminar-fees__entry">
                              <div className="yowan-event-detail-seminar-fees__item">
                                <div className="yowan-event-detail-seminar-fees__body">
                                  {item.scopeLabel ? (
                                    <span className="yowan-event-detail-seminar-fees__scope">{item.scopeLabel}</span>
                                  ) : null}
                                  <span className="yowan-event-detail-seminar-fees__name">{item.title}</span>
                                </div>
                                <span className="yowan-event-detail-seminar-fees__price">{item.feeLabel}</span>
                              </div>
                              {item.showFullParticipationUnavailableNotice ? (
                                <p className="yowan-event-detail-seminar-fees__notice">{fullParticipationUnavailableNotice}</p>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </section>
                    ) : null}
                    {renderPublicInstructors()}
                  </div>
                </div>
              </div>
            )) : (
              <div className="yowan-event-detail-class-layout">
                <div className="yowan-event-detail-schedules yowan-event-detail-schedules--primary">
                  {scheduleItems.length ? (
                    <div className="yowan-event-detail-schedules__grid">
                      {scheduleItems.map((schedule, index) => (
                        <article key={schedule.id} className="yowan-event-detail-schedules__card">
                          <p className="yowan-event-detail-schedules__eyebrow">開催情報 {index + 1}</p>
                          <h3 className="yowan-event-detail-schedules__title">{schedule.title}</h3>
                          <dl className="yowan-event-detail-schedules__definition">
                            <div>
                              <dt>場所</dt>
                              <dd>{schedule.location}</dd>
                            </div>
                            <div>
                              <dt>定員</dt>
                              <dd>{schedule.capacity}</dd>
                            </div>
                            <div>
                              <dt>費用</dt>
                              <dd>{schedule.fee}</dd>
                            </div>
                            <div>
                              <dt>講師</dt>
                              <dd>{schedule.instructor}</dd>
                            </div>
                          </dl>
                          {schedule.locationNote ? (
                            <p className="yowan-event-detail-schedules__note">{schedule.locationNote}</p>
                          ) : null}
                        </article>
                      ))}
                    </div>
                  ) : (
                    <p className="yowan-event-detail-schedules__empty">開催情報は準備中です。</p>
                  )}
                  {content.calendarUrl ? (
                    <a
                      className="yowan-event-detail-schedules__calendar"
                      href={content.calendarUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <CalendarTodayIcon className="yowan-event-detail-schedules__calendar-icon" aria-hidden="true" fontSize="small" />
                      カレンダーを見る
                    </a>
                  ) : null}
                </div>

                <div className="yowan-event-detail-content__guidance-copy yowan-event-detail-content__guidance-copy--class c-surface-content">
                  <h2 className="yowan-event-detail-content__guidance-title">教室について</h2>
                  {content.body.guidance.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}

                  {renderPublicInstructors()}
                </div>
              </div>
            )}
          </div>

          <div className="yowan-event-detail-form">
            {canSubmitApplication ? (
              <button
                type="button"
                className="c-form__submit yowan-event-detail-form__toggle"
                onClick={() => setFormOpen((prev) => !prev)}
              >
                {content.form.buttonLabel}
              </button>
            ) : (
              <p className="yowan-event-detail-form__message">{applicationUnavailableMessage}</p>
            )}

            {formOpen && canSubmitApplication ? (
              <form
                className="yowan-event-detail-form__panel c-surface-content c-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <label className="c-form__field">
                  <span className="c-form__label">
                    {content.form.labels.name}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="text"
                    readOnly={seminarMemberReadOnlyFields.name === true}
                    aria-readonly={seminarMemberReadOnlyFields.name === true ? 'true' : undefined}
                    style={seminarMemberReadOnlyFields.name === true ? seminarMemberReadOnlyControlStyle : undefined}
                    {...register('name', { required: content.form.errors.required })}
                  />
                  {errors.name ? <em className="yowan-event-detail-form__error">{errors.name.message}</em> : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {content.form.labels.nameKana}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="text"
                    readOnly={seminarMemberReadOnlyFields.name_kana === true}
                    aria-readonly={seminarMemberReadOnlyFields.name_kana === true ? 'true' : undefined}
                    style={seminarMemberReadOnlyFields.name_kana === true ? seminarMemberReadOnlyControlStyle : undefined}
                    {...register('name_kana', { required: content.form.errors.required })}
                  />
                  {errors.name_kana ? (
                    <em className="yowan-event-detail-form__error">{errors.name_kana.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {content.form.labels.email}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="email"
                    readOnly={seminarMemberReadOnlyFields.email === true}
                    aria-readonly={seminarMemberReadOnlyFields.email === true ? 'true' : undefined}
                    style={seminarMemberReadOnlyFields.email === true ? seminarMemberReadOnlyControlStyle : undefined}
                    {...register('email', { required: content.form.errors.required })}
                  />
                  {errors.email ? <em className="yowan-event-detail-form__error">{errors.email.message}</em> : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    {content.form.labels.phone}
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="tel"
                    readOnly={seminarMemberReadOnlyFields.phone === true}
                    aria-readonly={seminarMemberReadOnlyFields.phone === true ? 'true' : undefined}
                    style={seminarMemberReadOnlyFields.phone === true ? seminarMemberReadOnlyControlStyle : undefined}
                    {...register('phone', { required: content.form.errors.required })}
                  />
                  {errors.phone ? <em className="yowan-event-detail-form__error">{errors.phone.message}</em> : null}
                </label>

                {isSeminarDetail ? (
                  <>
                    <div className="c-form__field c-form__field--choice">
                      <span className="c-form__label">
                        {content.form.labels.gender}
                        <em className="c-form__required">*</em>
                      </span>
                      <div className="c-form__choices">
                        {content.form.options.gender.map((option) => {
                          const optionValue = typeof option === 'string' ? option : option.value;
                          const optionLabel = typeof option === 'string' ? option : option.label;

                          return (
                          <label key={optionValue} className="c-form__choice">
                            <input
                              type="radio"
                              value={optionValue}
                              {...register('gender', { required: content.form.errors.required })}
                            />
                            <span>{optionLabel}</span>
                          </label>
                          );
                        })}
                      </div>
                      {errors.gender ? <em className="yowan-event-detail-form__error">{errors.gender.message}</em> : null}
                    </div>

                    <label className="c-form__field">
                      <span className="c-form__label">
                        {content.form.labels.ageRange}
                        <em className="c-form__required">*</em>
                      </span>
                      <select
                        className="c-form__control"
                        {...register('age_range', { required: content.form.errors.required })}
                        defaultValue=""
                      >
                        <option value="" disabled>
                          選択してください
                        </option>
                        {content.form.options.ageRange.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {errors.age_range ? (
                        <em className="yowan-event-detail-form__error">{errors.age_range.message}</em>
                      ) : null}
                    </label>
                  </>
                ) : null}

                {isSeminarDetail ? (
                  <div className="c-form__field c-form__field--choice">
                    <span className="c-form__label">
                      {content.form.labels.preferredSlot}
                      <em className="c-form__required">*</em>
                    </span>
                    <div className="yowan-event-detail-form__seminar-choice">
                      {isGroupedSeminarSeries ? (
                        <div className="c-form__choices yowan-event-detail-form__seminar-choice-root">
                          {canUseSeminarSeriesFullParticipation(apiRecord) ? (
                            <label className="c-form__choice yowan-event-detail-form__slot-choice">
                              <input
                                type="radio"
                                name="seminar-participation-mode"
                                value="series"
                                checked={seminarParticipationMode === 'series'}
                                onChange={() => setSeminarRootParticipationMode('series')}
                              />
                              <span>
                                {apiRecord?.series?.title || content.body.title}に通しで参加する
                                {formatSeminarApplyFeeSuffix(apiRecord?.seriesFullParticipationFeeLabel || apiRecord?.fullParticipationFeeLabel)}
                              </span>
                            </label>
                          ) : null}
                          <label className="c-form__choice yowan-event-detail-form__slot-choice">
                            <input
                              type="radio"
                              name="seminar-participation-mode"
                              value="custom"
                              checked={seminarParticipationMode === 'custom'}
                              onChange={() => setSeminarRootParticipationMode('custom')}
                            />
                            <span>セミナーごとに選択する</span>
                          </label>
                        </div>
                              ) : null}
                      {seminarParticipationMode === 'custom' ? (
                        <div className="yowan-event-detail-form__seminar-sections">
                          {seminarSelectionSections.map((section, sectionIndex) => {
                            const sectionMode = seminarSectionModes[section.id] || getDefaultSeminarSectionMode(section, isGroupedSeminarSeries);
                            const selectedSlotIds = seminarSlotSelections[section.id] || [];
                            const sectionTitle = section.title || (isGroupedSeminarSeries ? `セミナー ${sectionIndex + 1}` : content.body.title);

                            return (
                              <div key={section.id} className="yowan-event-detail-form__seminar-section">
                                <div className="yowan-event-detail-form__seminar-section-header">
                                  <span className="yowan-event-detail-form__seminar-section-title">{sectionTitle}</span>
                                  {section.subtitle ? (
                                    <span className="yowan-event-detail-form__seminar-section-subtitle">{section.subtitle}</span>
                                  ) : null}
                                </div>
                                <div className="c-form__choices yowan-event-detail-form__seminar-section-modes">
                                  {isGroupedSeminarSeries ? (
                                    <label className="c-form__choice yowan-event-detail-form__slot-choice">
                                      <input
                                        type="radio"
                                        name={`seminar-section-mode-${section.id}`}
                                        value="none"
                                        checked={sectionMode === 'none'}
                                        onChange={() => setSeminarSectionMode(section.id, 'none')}
                                      />
                                      <span>参加しない</span>
                                    </label>
                                  ) : null}
                                  {canUseSeminarSectionFullParticipation(section) && section.slots.length > 1 ? (
                                    <label className="c-form__choice yowan-event-detail-form__slot-choice">
                                      <input
                                        type="radio"
                                        name={`seminar-section-mode-${section.id}`}
                                        value="event"
                                        checked={sectionMode === 'event'}
                                        onChange={() => setSeminarSectionMode(section.id, 'event')}
                                      />
                                      <span>
                                        このセミナーに通しで参加する
                                        {formatSeminarApplyFeeSuffix(section.fullParticipationFeeLabel)}
                                      </span>
                                    </label>
                                  ) : null}
                                  <label className="c-form__choice yowan-event-detail-form__slot-choice">
                                    <input
                                      type="radio"
                                      name={`seminar-section-mode-${section.id}`}
                                      value="slots"
                                      checked={sectionMode === 'slots'}
                                      onChange={() => setSeminarSectionMode(section.id, 'slots')}
                                    />
                                    <span>講座枠を選択して参加する</span>
                                  </label>
                                </div>
                                {sectionMode === 'slots' ? (
                                  <div className="c-form__choices yowan-event-detail-form__slot-choices">
                                    {section.slots.map((slot) => (
                                      <label key={slot.id} className="c-form__choice yowan-event-detail-form__slot-choice">
                                        <input
                                          type="checkbox"
                                          value={slot.id}
                                          checked={selectedSlotIds.includes(slot.id)}
                                          onChange={() => toggleSeminarSlotSelection(section.id, slot.id)}
                                        />
                                        <span>{formatSlotLabel(slot)}</span>
                                      </label>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                      {!seminarApplicationOptions.length ? (
                        <p className="yowan-event-detail-form__message">現在お申込み可能な講座枠はありません。</p>
                      ) : null}
                    </div>
                    {errors.preferred_slot ? (
                      <em className="yowan-event-detail-form__error">{errors.preferred_slot.message}</em>
                    ) : null}
                  </div>
                ) : classApplicationOptions.length <= 1 ? (
                  <label className="c-form__field">
                    <span className="c-form__label">
                      {content.form.labels.preferredSchedule || '希望する開催情報'}
                      <em className="c-form__required">*</em>
                    </span>
                    <input
                      className="c-form__control"
                      type="text"
                      value={formatClassApplicationScheduleLabel(classApplicationOptions[0])}
                      readOnly
                      aria-readonly="true"
                    />
                    <input
                      type="hidden"
                      value={classApplicationOptions[0]?.id || ''}
                      {...register('preferred_schedule', { required: content.form.errors.required })}
                    />
                    {errors.preferred_schedule ? (
                      <em className="yowan-event-detail-form__error">{errors.preferred_schedule.message}</em>
                    ) : null}
                  </label>
                ) : (
                  <label className="c-form__field">
                    <span className="c-form__label">
                      {content.form.labels.preferredSchedule || '希望する開催情報'}
                      <em className="c-form__required">*</em>
                    </span>
                    <select
                      className="c-form__control"
                      {...register('preferred_schedule', { required: content.form.errors.required })}
                      defaultValue=""
                    >
                      <option value="" disabled>
                        選択してください
                      </option>
                      {classApplicationOptions.map((schedule) => (
                        <option key={schedule.id} value={schedule.id}>
                          {formatClassApplicationScheduleLabel(schedule)}
                        </option>
                      ))}
                    </select>
                    {errors.preferred_schedule ? (
                      <em className="yowan-event-detail-form__error">{errors.preferred_schedule.message}</em>
                    ) : null}
                  </label>
                )}

                {!isSeminarDetail ? (
                  <label className="c-form__field">
                    <span className="c-form__label">{content.form.labels.preferredDate || '希望日'}</span>
                    <input
                      className="c-form__control"
                      type="date"
                      {...register('preferred_date')}
                    />
                    {errors.preferred_date ? (
                      <em className="yowan-event-detail-form__error">{errors.preferred_date.message}</em>
                    ) : null}
                  </label>
                ) : null}

                <label className="c-form__field">
                  <span className="c-form__label">{content.form.labels.message}</span>
                  <textarea
                    className="c-form__control c-form__control--textarea"
                    rows="7"
                    maxLength={applicationMessageMaxLength}
                    {...register('message', {
                      maxLength: {
                        value: applicationMessageMaxLength,
                        message: `メッセージは${applicationMessageMaxLength}文字以内で入力してください。`,
                      },
                    })}
                  />
                  {errors.message ? (
                    <em className="yowan-event-detail-form__error">{errors.message.message}</em>
                  ) : null}
                </label>

                {isSubmitting ? (
                  <p className="yowan-event-detail-form__message" aria-live="polite">
                    <LoadingSpinner inline size="sm" />
                    <span>{applicationSubmittingMessage}</span>
                  </p>
                ) : null}

                <button type="submit" className="c-form__submit" disabled={isSubmitting || !canSubmitApplication}>
                  {isSubmitting ? (
                    <>
                      <LoadingSpinner inline size="sm" />
                      <span>申込中です。</span>
                    </>
                  ) : content.form.submitLabel}
                </button>

                {!canSubmitApplication ? (
                  <p className="yowan-event-detail-form__message">{applicationUnavailableMessage}</p>
                ) : null}

                {submitMessage ? (
                  <p className="yowan-event-detail-form__message">{submitMessage}</p>
                ) : null}
              </form>
            ) : null}
          </div>

        </PageContainer>
      </Section>
    </>
  );
};

export default EventDetailPage;
